import { ApiClientReturnType, KeyLabelType, Nullable, TableDataType } from '~/types';
import api from '../../http-common';
import { FixedPricePrefixType } from '../dashboard';
import { Farmer } from '../individualFilters';
import { ShowFarmerService } from './showFarmer';

const getFarmersList = (data?: FarmerFilterType): ApiClientReturnType<FarmersType> =>
  api.post('/farmers/index', data)

const getFarmers = (data: FarmerFilterListType): ApiClientReturnType<Farmer[]> =>
  api.post('/farmers/list', data)

const getFarmersListOptions = (): ApiClientReturnType<FarmersOptionsType> =>
  api.post('/farmers/get-index-data')

const getFarmerOptions = (): ApiClientReturnType<FarmerCreateOptionsType> =>
  api.post('/farmers/get-options-data')

const getFarmersCount = (data?: FarmerCountType): ApiClientReturnType<FarmersTabCountType> =>
  api.post('/farmers/count', data)

const createFarmer = (data: FarmerStoreType): ApiClientReturnType<void> =>
  api.post('/farmers/store', data)

const getFarmer = (id: string) =>
  api.post(`/farmers/${id}/edit`)

const blockFarmer = (id: string): ApiClientReturnType<void> =>
  api.post(`/farmers/${id}/block`)

const deleteFarmer = (id: string): ApiClientReturnType<void> =>
  api.post(`/farmers/${id}/delete`)

const checkSupplierNo = (data: { supplierNo: string, id?: string | number }): ApiClientReturnType<1 | void> =>
  api.post('/farmers/check-supplier-no', data)

const unblockFarmer = (id: string): ApiClientReturnType<void> =>
  api.post(`/farmers/${id}/unblock`)

const updateFarmer = (id: string, data: FarmerStoreType & { id: string | number }): ApiClientReturnType<1 | void> =>
  api.post(`/farmers/${id}/update`, data)

const getFarmerFile = (id: string): ApiClientReturnType<string> =>
  api.post(`/farmers/${id}/get-file`)

const getFarmerProfileData = (id: string): ApiClientReturnType<FarmerShowProfileType> =>
  api.post(`/farmers/${id}/get-profile-data`)

const getFarmerFinancialData = (id: string): ApiClientReturnType<FarmerFinancialType> =>
  api.post(`/farmers/${id}/get-financial-data`)

const getStates = (data: { countryId?: string }): ApiClientReturnType<StateType[]> =>
  api.post('/farmers/get-states', data)

const getZipDistrictState = (data: { zip: string }): ApiClientReturnType<Nullable<ZipType>> =>
  api.post('/farmers/check-zip', data)

const getTradingMonthDetails = (id: string, prefix: FixedPricePrefixType, data): ApiClientReturnType<TradingMonthsType> =>
  api.post(`/farmers/${id}/${prefix}/trading-months/details`, data)

// MGA factors

const getMgaFactorsOptions = (id: string) =>
  api.post(`/farmers/${id}/mga-factors/get-options-data`)

const getMgaFactors = (id: string) =>
  api.post(`/farmers/${id}/mga-factors/edit`)

const updateMgaFactors = (id: string, data) =>
  api.post(`/farmers/${id}/mga-factors/update`, data)

const getDistrictList = (data?): ApiClientReturnType<DistrictListType[]> =>
  api.post('/districts/list', data)

// Articles

const getArticles = (id: string, data?) =>
  api.post(`/farmers/${id}/articles`, data)

const getBookings = (farmerId: string, idArticle: string, data?) =>
  api.post(`/farmers/${farmerId}/articles/${idArticle}/remove/get-bookings`, data)

const getArticleList = () =>
  api.post('/milk-money/shop/articles/list')

const addArticle = (id: string, data?) =>
  api.post(`/farmers/${id}/articles/add`, data)

const removeArticle = (id: string, idArticle: string, data?) =>
  api.post(`/farmers/${id}/articles/${idArticle}/remove`, data)

const getFarmerArticleBookings = (farmerId: string, articleId: string, data?) =>
  api.post(`/farmers/${farmerId}/articles/${articleId}/get-bookings`, data)

const editFarmerArticle = (id: string, idArticle: string) =>
  api.post(`/farmers/${id}/articles/${idArticle}/edit`)

const updateFarmerArticle = (idFarmer: string, idArticle: string, data?) =>
  api.post(`/farmers/${idFarmer}/articles/${idArticle}/update`, data)

// Booking historie

const bookingHistorySettings = (id: string) =>
  api.post(`farmers/${id}/article-bookings/get-settings-data`)

const bookingHistoryList = (id: string, data?) =>
  api.post(`farmers/${id}/article-bookings`, data)

const deleteBookingHistory = (farmerId: string, articleBookingId: string) =>
  api.post(`farmers/${farmerId}/article-bookings/${articleBookingId}/remove`)

// Dairy data

const getDairyOptions = (id: string, modulePrefix = CO2_DEFAULT_PREFIX): ApiClientReturnType<KeyLabelType[]> =>
  api.post(`/farmers/${id}/${modulePrefix}/get-options-data`)

const getDairyData = (id: string, data: { year: string | number }, modulePrefix = CO2_DEFAULT_PREFIX): ApiClientReturnType<DairyDataType> =>
  api.post(`/farmers/${id}/${modulePrefix}/get-data`, data)

const updateDairyData = (
  id: string,
  data: { amounts: DairyDataAmountType[] } | { notDeliveredToDairy: number, year: string | number },
  modulePrefix = CO2_DEFAULT_PREFIX
): ApiClientReturnType<void> =>
  api.post(`/farmers/${id}/${modulePrefix}/update`, data)

export const FarmersService = {
  getFarmersList,
  getFarmersListOptions,
  getFarmer,
  getFarmerFinancialData,
  getFarmerProfileData,
  createFarmer,
  updateFarmer,
  getFarmersCount,
  getFarmerOptions,
  blockFarmer,
  unblockFarmer,
  getFarmers,
  getTradingMonthDetails,
  getFarmerFile,
  checkSupplierNo,
  deleteFarmer,
  getMgaFactors,
  getMgaFactorsOptions,
  updateMgaFactors,
  getDistrictList,
  getArticles,
  removeArticle,
  addArticle,
  editFarmerArticle,
  updateFarmerArticle,
  getArticleList,
  getBookings,
  bookingHistoryList,
  bookingHistorySettings,
  getFarmerArticleBookings,
  deleteBookingHistory,
  getStates,
  getZipDistrictState,
  getDairyOptions,
  getDairyData,
  updateDairyData,
  ...ShowFarmerService
}
export interface FarmersOptionsType {
  types: KeyLabelType[];
  farmerGroups: FarmerGroup[];
  farmersImport: number;
  states: StateType[];
  milkTypes: MilkType[];
}
export interface FarmerGroup {
  id: string;
  name: string;
  amountPercent: string;
}
export interface MilkType {
  id: number;
  default: number;
  abbreviation: string;
  name: string;
  label: string;
  key?: string;
  isBio: number;
  milkTypeId: number;
  languageId: number;
}
export interface FarmersType {
  rows: Row[];
  total: number;
  filtered: number;
}
export interface Row {
  id: string;
  companyName: string;
  supplierNo: null | string;
  farmerGroupName: string;
  amount: number;
  parentId: null | string;
  status: number;
  terminationDate: null | string;
  lastDeliveryDate: null | string;
  phonePrefix: null | string;
  phoneNumber: null | string;
  confirmedAmount: number;
  districtId: number | null;
  yearlyAmount: number;
  usersCount: number;
}
export interface FarmersTabCountType {
  all_farmers: number;
  active_farmers: number;
  sub_farmers: number;
  farmers_in_clarification: number;
  inactive_farmers: number;
}

export type MilkTypeCharacteristic = {
  id: number;
  slug: string;
  status: number;
  label: string;
};

export type TransportArea = {
  id: number;
  status: number;
  code: string;
  icon: string;
  name: string;
  abbreviation: string;
  label: string;
  transportAreaId: number;
  languageId: number;
};

export type RestrictionPeriodDay = {
  id: number;
  restrictionPeriodId: number;
  day: number;
  timeFrom: string;
  timeTo: string;
  maxTemp: string;
  label: string;
};

export type RestrictionPeriod = {
  id: number;
  name: string;
  status: number;
  days: RestrictionPeriodDay[];
};

export type OperatingForm = {
  id: number;
  status: number;
  code: string;
  icon: string;
  name: string;
  abbreviation: string;
  label: string;
  operatingFormId: number;
  languageId: number;
};

export interface FarmerCreateOptionsType {
  countries: Country[];
  farmerTypes: KeyLabelType[];
  amountTypes: KeyLabelType[];
  milkTypes: MilkType[];
  defaultCountry: number;
  managers: { id: string, name: string, agisId: string }[];
  certifications: any[];
  collectionCycles: CollectionOptionsType[];
  collectionMethods: { id: string, label: string }[];
  contractTypes: any[];
  collectionPoints: CollectionOptionType[];
  collectionSites: CollectionOptionType[];
  milkTypeCharacteristics: MilkTypeCharacteristic[];
  transportAreas: TransportArea[];
  milkPurchases: KeyLabelType[];
  restrictionPeriods: RestrictionPeriod[];
  productionZones: any[];
  operatingForms: OperatingForm[];
}

export type CollectionOptionType = {
  id: string
  name: string
  evs: Nullable<string>
}
export interface CollectionOptionsType {
  id: number;
  status: number;
  code: string;
  icon: string;
  name: string;
  abbreviation: string;
  label: string;
  collectionCycleId: number;
  languageId: number;
}

export interface Country {
  id: number;
  iso: string;
  phoneCode: string;
  label: string;
}

export interface District {
  id: number;
  name: string;
  number: string;
  stateName: string;
}

type DistrictType = {
  id: number,
  stateId: number,
  name: string,
  zip: string
}

type AgricultureDistrictType = {
  id: number,
  stateId: number,
  name: string,
  number: string
}
export interface StateType {
  abbreviation: string
  agricultureDistricts: AgricultureDistrictType[]
  administrativeDistricts: Omit<AgricultureDistrictType, 'number'>[]
  countryId: number
  districts: DistrictType[]
  id: number
  label: string
  name: number
}
export interface DistrictListType {
  id: number;
  stateId: number;
  zip: string;
  name: string;
}
export interface DairyDataAmountType {
  month: Date;
  amount: number | null;
  fat: null | string;
  protein: null | string;
  netPrice: null | string;
}

export interface DairyDataType {
  notDeliveredToDairy: number
  amounts: DairyDataAmountType[]
}

export type FarmerFilterType = {
  page: number,
  rows?: number,
  search: string,
  farmerGroupId?: string | number
  periodFrom?: string
  periodTo?: string
}

export type FarmerFilterListType = FarmerFilterType & {
  completed?: string,
  isMain?: Nullable<number>,
  selected?: string[]
}

export type FarmerCountType = {
  type: string,
  id: Nullable<string>,
  value: boolean,
  idKey: string
}

export interface FarmerStoreType {
  amounts?: Amount[];
  countryId: number;
  supplierNo: string;
  image?: string;
  companyName: string;
  milkTypeId: number;
  mprPin?: string;
  farmerType: string;
  streetName?: string;
  streetNumber?: string;
  zip: string;
  city: string;
  stateId: number;
  amountType: string;
  parentId?: string;
  phonePrefix?: number;
  phoneNumber?: string;
  telefaxPrefix?: number;
  telefaxNumber?: string;
  terminationDate?: Date;
  lastDeliveryDate?: Date;
  districtId?: number;
  agricultureDistrictId?: number;
}

export interface Amount {
  month: Date;
  amount: string;
}

export interface FarmerShowProfileType {
  tradingVolumeTypes: TradingVolumeType[];
  farmer: FarmerProfileType;
}

export type FarmerProfileType = {
  id: string;
  supplierNo: string;
  internalId: Nullable<string>;
  lastDeliveryDate: Nullable<string>;
  terminationDate: Nullable<string>;
  phonePrefix: string;
  phoneNumber: string;
  telefaxPrefix: Nullable<string>;
  telefaxNumber: Nullable<string>;
  telefaxPrefixCountryPhoneCode?: Nullable<string>;
  farmerGroupId: string;
  parentId: Nullable<string>;
  amount: number;
  companyName: string;
  amountPercent: string;
  minimumContingent: number;
  name: string;
  tradingAmount: string;
  monthlyTradingAmount: string;
  addressableId: string;
  streetName: string;
  streetNumber: string;
  zip: string;
  city: string;
  countryId: number;
  localDistrict: Nullable<string>;
  stateId: number;
  districtId: Nullable<string>;
  agricultureDistrictId: number;
  countryIso: string;
  countryLabel: string;
  stateName: string;
  abbreviation: string;
  standardFat: string;
  standardProtein: string;
  fatPrice: string;
  proteinPrice: string;
  label: string;
  district: Nullable<string>;
  agricultureDistrictName: string;
  agricultureDistrictNumber: string;
  phonePrefixCountryId: number;
  phonePrefixCountryPhoneCode: string;
  phonePrefixCountryLabel: string;
  telefaxPrefixCountry: Nullable<string>;
  mainFarmer: Nullable<{
    id: string
    supplierNo: string | number
    companyName: string
  }>;
  subFarmers: any[];
}

export interface TradingVolumeType {
  id: string;
  percent: string;
  tradingAmount: number;
  code: string;
  icon: string;
  name: string;
  label: string;
  tradingVolumeTypeId: string;
  languageId: number;
}

export interface FarmerFinancialType {
  settings: Setting[];
}

export interface Setting {
  id: number;
  farmerId: string;
  bankName: string;
  bic: string;
  iban: string;
  ibanValid: number;
  status: number;
}

export interface ZipType {
  id: number;
  stateId: number;
  zip: string;
  name: string;
  countryId: number;
  label: string;
}

export type TradingMonthsType = TableDataType<TradingMonthType>

export interface TradingMonthType {
  month: Date;
  amount: number;
  confirmedAmount: number;
  offeredAmount: number;
  remainingAmount: number;
  total: number;
  avgPrice: number;
}

export interface FarmerShowType {
  tradingAmountStatuses: TradingAmountStatus[];
  settings: Settings;
  farmer: FarmerShowDataType;
}

export interface FarmerShowDataType {
  id: string;
  supplierNo: string;
  companyName: string;
  status: number;
  milkTypeId: number;
  createdAt: Date;
  default: number;
  isBio: number;
  additionalInfo: null;
  abbreviation: string;
  name: string;
  label: string;
  languageId: number;
}

export interface Settings {
  supplierNoUnique: number;
}

export interface TradingAmountStatus {
  key: string;
  label: string;
}

export type FarmerUsersType = TableDataType<FarmerUserType>

export interface FarmerUserType {
  id: string;
  supplierNo: string;
  companyName: string;
  name: string;
  email: string;
  status: number;
  lastLogin: null | string;
  verified: number;
  farmerId: string;
  mobilePrefix: number;
  mobileNumber: string;
  mobilePrefixCountryId: number;
  mobilePrefixCountryIso: string;
  mobilePrefixCountryPhoneCode: string;
  mobilePrefixCountryLabel: string;
}

export type FarmerUserFilType = {
  page: number,
  type: string,
  search: string,
  status: Nullable<string>,
  rows: number
  farmerId: string
}

export const CO2_DEFAULT_PREFIX = 'co2-footprint'
export const CO2_KLIMA_PREFIX = 'climate-check-footprint'

